import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Chip,
  Paper,
  IconButton,
} from "@mui/material";
import { FlexRow } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import { AiOutlineAudit, AiOutlineClose } from "react-icons/ai";
import { MdAccessTime, MdHistory } from "react-icons/md";
import api from "../../../api/axiosInstance";
import theme from "../../../theme";
import { format } from "date-fns";
import { getRoleName } from "../../../data";
import { UserRolesEnum } from "../../../typings";

interface AuditTrailModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
}

interface AuditTrailEntry {
  id: number;
  action_performed_by: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  message: string;
  created_at: string;
}

interface AuditTrailResponse {
  success: boolean;
  messages: string[];
  data: AuditTrailEntry[];
  pagination: null;
}

export default function AuditTrailModal({
  open,
  onClose,
  scholarshipApplicationId,
}: AuditTrailModalProps) {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [auditTrail, setAuditTrail] = useState<AuditTrailEntry[]>([]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      const response = await api.get<AuditTrailResponse>(
        "/scholarship-applications/history",
        {
          params: {
            scholarship_application_id: scholarshipApplicationId,
          },
        }
      );

      if (response.data.success) {
        setAuditTrail(response.data.data);
      }
    } catch (error: any) {
      console.error("Error fetching audit trail:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to fetch audit trail. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAuditTrail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scholarshipApplicationId]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case UserRolesEnum.SUPER_ADMIN:
        return theme.palette.colors.purple["600"];
      case UserRolesEnum.ADMIN:
        return theme.palette.colors.blue["600"];
      case UserRolesEnum.STUDENT:
        return theme.palette.colors.green["600"];
      case UserRolesEnum.INTERVIEWER:
        return theme.palette.colors.orange["600"];
      case UserRolesEnum.MENTOR:
        return theme.palette.colors.indigo["600"];
      case UserRolesEnum.ACCOUNTANT:
        return theme.palette.colors.blueGray["600"];
      default:
        return theme.palette.colors.gray["600"];
    }
  };

  const getRoleBackgroundColor = (role: string) => {
    switch (role) {
      case UserRolesEnum.SUPER_ADMIN:
        return "rgba(105, 56, 239, 0.1)";
      case UserRolesEnum.ADMIN:
        return "rgba(59, 130, 246, 0.1)";
      case UserRolesEnum.STUDENT:
        return "rgba(16, 185, 129, 0.1)";
      case UserRolesEnum.INTERVIEWER:
        return "rgba(245, 158, 11, 0.1)";
      case UserRolesEnum.MENTOR:
        return "rgba(99, 102, 241, 0.1)";
      case UserRolesEnum.ACCOUNTANT:
        return "rgba(107, 114, 128, 0.1)";
      default:
        return "rgba(107, 114, 128, 0.1)";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy 'at' hh:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "8px",
          maxWidth: "750px",
          width: "100%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "20px 24px",
          fontSize: "20px",
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.colors.gray["200"]}`,
          backgroundColor: "white",
        }}
      >
        <FlexRow align="center" justify="space-between">
          <FlexRow align="center">
            <AiOutlineAudit
              size={22}
              style={{
                marginRight: "10px",
                color: theme.palette.primary.main,
              }}
            />
            <span>Audit Trail</span>
          </FlexRow>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            sx={{
              color: theme.palette.colors.gray["600"],
              "&:hover": {
                backgroundColor: theme.palette.colors.gray["100"],
              },
            }}
          >
            <AiOutlineClose size={18} />
          </IconButton>
        </FlexRow>
      </DialogTitle>
      <DialogContent
        style={{
          padding: "20px 24px",
          backgroundColor: "#fafafa",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="250px"
          >
            <CircularProgress size={36} />
          </Box>
        ) : auditTrail.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="250px"
            style={{ gap: "12px" }}
          >
            <MdHistory
              size={56}
              style={{ color: theme.palette.colors.gray["300"] }}
            />
            <Typography
              variant="subtitle1"
              color="textSecondary"
              style={{ fontWeight: 600 }}
            >
              No audit trail available
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
              style={{ maxWidth: "300px" }}
            >
              There are no recorded activities for this application yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: "relative", py: 1 }}>
            {/* Timeline line */}
            <Box
              sx={{
                position: "absolute",
                left: "20px",
                top: "8px",
                bottom: "8px",
                width: "2px",
                backgroundColor: theme.palette.colors.gray["300"],
                zIndex: 0,
              }}
            />

            {/* Audit entries */}
            <Box sx={{ position: "relative", zIndex: 1 }}>
              {auditTrail.map((entry, index) => (
                <Box key={entry.id} sx={{ mb: index === auditTrail.length - 1 ? 0 : 2.5, position: "relative" }}>
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      width: "14px",
                      height: "14px",
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      border: `3px solid ${theme.palette.primary.light}`,
                      zIndex: 2,
                    }}
                  />
                  
                  <Paper
                    elevation={0}
                    sx={{
                      ml: 5,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: "white",
                      border: `1px solid ${theme.palette.colors.gray["200"]}`,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.light,
                        boxShadow: `0 2px 8px rgba(0, 0, 0, 0.08)`,
                      },
                    }}
                  >
                    <FlexRow align="flex-start" justify="space-between" sx={{ mb: 1.5 }}>
                      <FlexRow style={{ gap: "10px", flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: getRoleColor(entry.action_performed_by.role),
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(entry.action_performed_by.name)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.colors.gray["900"],
                              fontSize: "14px",
                            }}
                          >
                            {entry.action_performed_by.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.colors.gray["500"],
                              fontSize: "12px",
                            }}
                          >
                            {entry.action_performed_by.email}
                          </Typography>
                        </Box>
                      </FlexRow>
                      <Chip
                        label={getRoleName(entry.action_performed_by.role)}
                        size="small"
                        sx={{
                          backgroundColor: getRoleBackgroundColor(entry.action_performed_by.role),
                          color: getRoleColor(entry.action_performed_by.role),
                          fontWeight: 600,
                          fontSize: "11px",
                          height: "22px",
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                      />
                    </FlexRow>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.colors.gray["700"],
                        mb: 1,
                        fontSize: "13px",
                        lineHeight: 1.5,
                      }}
                    >
                      {entry.message}
                    </Typography>

                    <FlexRow
                      align="center"
                      style={{ gap: "6px" }}
                      sx={{
                        color: theme.palette.colors.gray["500"],
                      }}
                    >
                      <MdAccessTime size={14} />
                      <Typography variant="caption" sx={{ fontSize: "12px" }}>
                        {formatDate(entry.created_at)}
                      </Typography>
                    </FlexRow>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
